"use client"
import { cn } from "../../lib"

type GlyphVariant =
  | "diamond"
  | "tricolor"
  | "balls"
  | "balls-arc"
  | "staggered-bars"
  | "corner-cluster"
  | "corner-scatter"
  | "divider-spread"
  | "burst-toss"
  | "dropped-catch"

type GlyphSvgProps = {
  variant: GlyphVariant
  height: string
}

function GlyphSvg({ variant, height }: GlyphSvgProps) {
  const s = { height, width: "auto" } as const

  const glyphs = {
    diamond: (
      <svg viewBox="3 3 34 34" fill="none" style={s}>
        <rect
          x="20"
          y="4"
          width="22.6"
          height="22.6"
          rx="2"
          transform="rotate(45 20 4)"
          className="fill-spark-orange"
        />
      </svg>
    ),
    tricolor: (
      <svg viewBox="-2 3 42 27" fill="none" style={s}>
        <rect
          x="12"
          y="4"
          width="18"
          height="18"
          rx="1.5"
          transform="rotate(45 12 4)"
          className="fill-spark-orange"
          opacity="0.85"
        />
        <rect
          x="28"
          y="6"
          width="16"
          height="16"
          rx="1.5"
          transform="rotate(45 28 6)"
          className="fill-spark-purple"
          opacity="0.85"
        />
        <rect
          x="20"
          y="10"
          width="13"
          height="13"
          rx="1.5"
          transform="rotate(45 20 10)"
          className="fill-spark-yellow"
          opacity="0.85"
        />
      </svg>
    ),
    balls: (
      <svg viewBox="5 4 39 35" fill="none" style={s}>
        <circle cx="14" cy="30" r="8" className="fill-spark-orange" />
        <circle cx="36" cy="26" r="7" className="fill-spark-purple" />
        <circle cx="26" cy="12" r="6.5" className="fill-spark-yellow" />
      </svg>
    ),
    "balls-arc": (
      <svg viewBox="0 1 60 40" fill="none" style={s}>
        <path
          d="M6 36 Q18 4 34 12 Q50 20 56 34"
          stroke="currentColor"
          strokeWidth="1"
          strokeDasharray="2.5 3.5"
          fill="none"
          opacity="0.25"
        />
        <circle cx="8" cy="32" r="7" className="fill-spark-orange" />
        <circle cx="32" cy="8" r="5.5" className="fill-spark-yellow" />
        <circle cx="52" cy="28" r="6.5" className="fill-spark-purple" />
      </svg>
    ),
    "staggered-bars": (
      <svg viewBox="2 5 26 28" fill="none" style={s}>
        <rect
          x="3"
          y="10"
          width="5"
          height="22"
          rx="2"
          className="fill-spark-orange"
        />
        <rect
          x="12.5"
          y="6"
          width="5"
          height="22"
          rx="2"
          className="fill-spark-purple"
        />
        <rect
          x="22"
          y="14"
          width="5"
          height="18"
          rx="2"
          className="fill-spark-yellow"
        />
      </svg>
    ),
    "corner-cluster": (
      <svg viewBox="-1 4 24 23" fill="none" style={s}>
        <rect
          x="6"
          y="6"
          width="8"
          height="8"
          rx="1"
          transform="rotate(45 6 6)"
          className="fill-spark-orange"
          opacity="0.9"
        />
        <rect
          x="6"
          y="18"
          width="5.5"
          height="5.5"
          rx="0.5"
          transform="rotate(45 6 18)"
          className="fill-spark-purple"
          opacity="0.8"
        />
        <rect
          x="18"
          y="6"
          width="5.5"
          height="5.5"
          rx="0.5"
          transform="rotate(45 18 6)"
          className="fill-spark-yellow"
          opacity="0.8"
        />
        <rect
          x="16"
          y="16"
          width="3.5"
          height="3.5"
          rx="0.5"
          transform="rotate(45 16 16)"
          className="fill-spark-orange"
          opacity="0.4"
        />
      </svg>
    ),
    "corner-scatter": (
      <svg viewBox="-1 0 34 37" fill="none" style={s}>
        <rect
          x="5"
          y="5"
          width="7"
          height="7"
          rx="1"
          transform="rotate(45 5 5)"
          className="fill-spark-orange"
          opacity="0.85"
        />
        <circle
          cx="8"
          cy="22"
          r="3.5"
          className="fill-spark-purple"
          opacity="0.6"
        />
        <circle
          cx="22"
          cy="8"
          r="3"
          className="fill-spark-yellow"
          opacity="0.55"
        />
        <rect
          x="18"
          y="18"
          width="4"
          height="4"
          rx="0.5"
          transform="rotate(45 18 18)"
          className="fill-spark-purple"
          opacity="0.3"
        />
        <rect
          x="28"
          y="5"
          width="3"
          height="3"
          rx="0.5"
          transform="rotate(45 28 5)"
          className="fill-spark-yellow"
          opacity="0.25"
        />
        <circle
          cx="5"
          cy="34"
          r="2"
          className="fill-spark-orange"
          opacity="0.25"
        />
      </svg>
    ),
    "divider-spread": (
      <svg
        viewBox="0 0 300 20"
        fill="none"
        preserveAspectRatio="xMidYMid meet"
        style={s}
      >
        <line
          x1="0"
          y1="10"
          x2="110"
          y2="10"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.2"
        />
        <rect
          x="120"
          y="4.5"
          width="8"
          height="8"
          rx="1"
          transform="rotate(45 120 4.5)"
          className="fill-spark-orange"
          opacity="0.85"
        />
        <circle
          cx="145"
          cy="10"
          r="5"
          className="fill-spark-purple"
          opacity="0.8"
        />
        <rect
          x="163"
          y="5"
          width="7"
          height="7"
          rx="1"
          transform="rotate(45 163 5)"
          className="fill-spark-yellow"
          opacity="0.85"
        />
        <line
          x1="182"
          y1="10"
          x2="300"
          y2="10"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.2"
        />
      </svg>
    ),
    "burst-toss": (
      <svg viewBox="3 0 40 47" fill="none" style={s}>
        <circle cx="25" cy="44" r="2" fill="currentColor" opacity="0.2" />
        <path
          d="M25 44 Q15 30 12 16"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          fill="none"
          opacity="0.1"
        />
        <path
          d="M25 44 Q28 28 34 14"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          fill="none"
          opacity="0.1"
        />
        <circle
          cx="10"
          cy="14"
          r="5.5"
          className="fill-spark-orange"
          opacity="0.85"
        />
        <circle
          cx="26"
          cy="6"
          r="5"
          className="fill-spark-purple"
          opacity="0.8"
        />
        <circle
          cx="38"
          cy="12"
          r="4.5"
          className="fill-spark-yellow"
          opacity="0.8"
        />
      </svg>
    ),
    "dropped-catch": (
      <svg viewBox="0 0 66 52" fill="none" style={s}>
        {/* orange fall arc */}
        <path
          d="M14 8 Q16 18 11 32"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          fill="none"
          opacity="0.12"
        />
        {/* purple fall arc */}
        <path
          d="M30 2 Q28 14 32 22"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          fill="none"
          opacity="0.12"
        />
        {/* yellow fall arc – follows all the way down */}
        <path
          d="M50 6 Q52 18 50 44"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="2 2"
          fill="none"
          opacity="0.12"
        />
        {/* purple – barely started falling */}
        <circle
          cx="32"
          cy="24"
          r="5"
          className="fill-spark-purple"
          opacity="0.75"
        />
        {/* orange – mid-fall */}
        <circle
          cx="11"
          cy="34"
          r="5"
          className="fill-spark-orange"
          opacity="0.85"
        />
        {/* roll trail */}
        <path
          d="M50 44 Q54 44.5 58 45"
          stroke="currentColor"
          strokeWidth="0.5"
          strokeDasharray="1.5 2"
          fill="none"
          opacity="0.15"
        />
        {/* yellow – on the floor, rolled further right */}
        <circle
          cx="58"
          cy="44"
          r="5"
          className="fill-spark-yellow"
          opacity="0.7"
        />
        {/* ground line */}
        <line
          x1="2"
          y1="50"
          x2="66"
          y2="50"
          stroke="currentColor"
          strokeWidth="0.75"
          opacity="0.1"
        />
      </svg>
    ),
  } as const

  return glyphs[variant]
}

type GlyphProps = {
  variant: GlyphVariant
  size?: number | string
  className?: string
}

export default function Glyph({
  variant,
  size = 24,
  className = "",
}: GlyphProps) {
  return (
    <span className={cn("inline-block", className)}>
      <GlyphSvg
        variant={variant}
        height={typeof size === "number" ? `${size}px` : size}
      />
    </span>
  )
}
